// eslint-disable-next-line no-unused-vars
import _ from "lodash";
// eslint-disable-next-line no-unused-vars
import React, { CSSProperties, useEffect, useRef, useState } from "react";
import HStack from "./HStack";
import XProtect from "./_gears/XProtect";

/**
 * @typedef {"Consolas" | "Arial" | "Palanquin" 
 * | "Georgia" | "serif" | "sans-serif" | "system-ui" 
 * | "cursive" | "Red Hat Display" | "Roboto"} fontFamily
 * 
 * @typedef {Object} xtextparams
 * @property {CSSProperties} style
 * @property {Number | String} width
 * @property {Number | String} height
 * @property {Number | String} minWidth
 * @property {Number | String} maxWidth
 * @property {Number | String} minHeight
 * @property {Number | String} maxHeight
 * @property {Number | String} lockWidth
 * @property {Number | String} lockHeight
 * @property {String} children
 * @property {"clip" | "ellipsis" | "initial" | "inherit" | "revert" | "revert-layer" | "unset" } ellipsisMode
 * @property {Number} fontSize
 * @property {Number} fontWeight
 * @property {fontFamily} fontFamily
 * @property {String} color
 * @property {Boolean} bold
 * @property {Boolean} italic
 * @property {"none" | "underline" | "overline" | "line-through" | "initial" | "inherit"} decoration
 * @property {Boolean} fullWidth
 * @property {Boolean} fullHeight
 * @property {Boolean} fitWidthToContent
 * @property {Boolean} fitHeightToContent
 * @property {Boolean} autoWidth
 * @property {Boolean} autoHeight
 * @property {Number} numberOfLines
 * @property {"[" | "|" | "]"} textAlign
 * @property {Boolean} noWrap
 * @property {Boolean} newLine
 * @property {"normal" | "break-word" | "anywhere" | "inherit" | "initial" | "revert" | "revert-layer" | "unset"} overflowWrap
 * @property {Number} zIndex
 * @property {Number} flexShrink
 * @property {Number} flexGrow
 * @property {Number | String} flexBasis
 * @property {"nowrap" | "wrap" | "wrap-reverse"} flexWrap
 * @property {Number} opacity
 * @property {Boolean} colorTransition
 * @property {Boolean} sizeTransition
 * @property {Boolean} positionTransition
 * @property {Boolean} allTransition
 * @property {Number} transitionDuration
 * @property {"capitalize" | "uppercase" | "lowercase" | "none"
 *  | "full-width" | "full-size-kana" | "math-auto"} textTransform
 * @property {Boolean} breakLines
 * @property {Boolean} autoAdjust
 * @property {Boolean} noSelect
 * @property {Number} lineHeight
 * @property {"horizontal-tb" | "vertical-rl" | "vertical-lr"} writingMode
 * @property {"[" | "]"} verticalTextDirection
 * @property {String} clipColor
 * @property {Boolean} rainbow
 * 
 * @param {xtextparams} param0
 * @returns 
 */
function XText({
  style, 
  children, 
  height = undefined,
  width = undefined,
  minWidth = undefined,
  maxWidth = undefined,
  minHeight = undefined,
  maxHeight = undefined, 
  ellipsisMode = undefined,
  fontSize = undefined,
  fontWeight = undefined,
  fontFamily = undefined,
  color = undefined,
  bold = false,
  italic = false,
  decoration = undefined,
  fullWidth = undefined,
  fullHeight = undefined,
  fitWidthToContent = undefined,
  fitHeightToContent = undefined,
  autoWidth = undefined,
  autoHeight = undefined,
  numberOfLines = 1,
  textAlign = undefined,
  noWrap = undefined,
  newLine = undefined,
  overflowWrap = undefined,
  zIndex = undefined,
  flexShrink = undefined,
  flexGrow = undefined,
  flexWrap = undefined,
  flexBasis = undefined,
  opacity = undefined,
  colorTransition = false,
  sizeTransition = false,
  positionTransition = false,
  allTransition = false,
  transitionDuration = 500,
  textTransform = undefined,
  lockWidth = undefined,
  lockHeight = undefined,
  breakLines = undefined,
  autoAdjust = false,
  noSelect = false,
  lineHeight = undefined,
  writingMode = undefined,
  verticalTextDirection = undefined,
  clipColor = undefined,
  rainbow = false,
  ...others
}){

  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [calFontSize, setCalFontSize] = useState(fontSize ?? 16);

  useEffect(() => {
    const adjustFontSize = () => {
      const container = containerRef.current;
      const text = textRef.current;
      let newFontSize = calFontSize;

      if (container && text) {
        if (text.scrollWidth > container.clientWidth && newFontSize > 0) {
          newFontSize--;
        }
        setCalFontSize(newFontSize);
      }
    };

    if(autoAdjust) {
      adjustFontSize();
      window.addEventListener("resize", adjustFontSize);

      return () => {
        window.removeEventListener("resize", adjustFontSize);
      };
    }
  }, [autoAdjust, containerRef, textRef, calFontSize]);

  let _addStyle = {};
  if(width !== undefined) _addStyle.width = width;
  if(height !== undefined) _addStyle.height = height;
  if(bold) _addStyle.fontWeight = "bold";
  if(italic) _addStyle.fontStyle = "italic";
  if(decoration !== undefined) _addStyle.textDecoration = decoration;
  if(fontFamily !== undefined) _addStyle.fontFamily = fontFamily;
  if(color !== undefined) _addStyle.color = color;
  if(fullWidth) _addStyle.width = "100%";
  if(fullHeight) _addStyle.height = "100%";
  if(fitWidthToContent) _addStyle.width = "fit-content";
  if(fitHeightToContent) _addStyle.height = "fit-content";
  if(autoWidth) _addStyle.width = "auto";
  if(autoHeight) _addStyle.height = "auto";
  if(minWidth !== undefined) _addStyle.minWidth = minWidth;
  if(maxWidth !== undefined) _addStyle.maxWidth = maxWidth;
  if(minHeight !== undefined) _addStyle.minHeight = minHeight;
  if(maxHeight !== undefined) _addStyle.maxHeight = maxHeight;

  if(textAlign){
    switch(textAlign){
      case "[": _addStyle.textAlign = "left"; break;
      case "|": _addStyle.textAlign = "center"; break;
      case "]": _addStyle.textAlign = "right"; break;
      default: break;
    }
  }
  _addStyle.whiteSpace = "pre-wrap";
  if(newLine) _addStyle.whiteSpace = "pre";
  if(noWrap) _addStyle.whiteSpace = "nowrap";

  if(!_.isEmpty(ellipsisMode)){
    _addStyle.textOverflow = ellipsisMode;
    _addStyle.overflow = "hidden";
    _addStyle.whiteSpace = "nowrap";
  }

  _addStyle.overflowWrap = overflowWrap;

  if(zIndex !== undefined) _addStyle.zIndex = zIndex;
  if(flexShrink !== undefined) _addStyle.flexShrink = flexShrink;
  if(flexGrow) _addStyle.flexGrow = flexGrow;
  if(flexWrap !== undefined) _addStyle.flexWrap = flexWrap;
  if(flexBasis !== undefined) _addStyle.flexBasis = flexBasis;
  if(opacity !== undefined) _addStyle.opacity = opacity;

  let transition = style?.transition?.split(",") ?? [];
  if(colorTransition){
    transition.push(`background-color ${transitionDuration}ms`);
    transition.push(`color ${transitionDuration}ms`);
    transition.push(`opacity ${transitionDuration}ms`);
  }else{
    if(color?.day && color?.night){
      transition.push(`color ${transitionDuration}ms`);
    }
  }
  if(positionTransition){
    transition.push(`top ${transitionDuration}ms`);
    transition.push(`left ${transitionDuration}ms`);
    transition.push(`right ${transitionDuration}ms`);
    transition.push(`bottom ${transitionDuration}ms`);
  }
  if(sizeTransition){
    transition.push(`width ${transitionDuration}ms`);
    transition.push(`height ${transitionDuration}ms`);
    transition.push(`min-width ${transitionDuration}ms`);
    transition.push(`min-height ${transitionDuration}ms`);
    transition.push(`max-width ${transitionDuration}ms`);
    transition.push(`max-height ${transitionDuration}ms`);
  }
  if(allTransition){
    transition.push(`all ${transitionDuration}ms`);
  }
  if(transition?.length > 0){
    _addStyle.transition = transition.join(", ");
  }

  if(textTransform){
    _addStyle.textTransform = textTransform;
  }

  if(lockHeight !== undefined){
    _addStyle.height = lockHeight;
    _addStyle.minHeight = lockHeight;
    _addStyle.maxHeight = lockHeight;
  }

  if(lockWidth !== undefined){
    _addStyle.width = lockWidth;
    _addStyle.minWidth = lockWidth;
    _addStyle.maxWidth = lockWidth;
  }

  if(lineHeight){
    _addStyle.lineHeight = lineHeight;
  }

  if(breakLines){
    _addStyle.whiteSpace = "collapse";
    _addStyle.lineHeight = lineHeight ?? 1.2;
  }

  if(noSelect){
    _addStyle.userSelect = "none";
  }

  if(!_.isUndefined(fontWeight)){
    _addStyle.fontWeight = fontWeight;
  }

  if(writingMode){
    _addStyle.writingMode = writingMode;
    _addStyle.textOrientation = "mixed"; // Default to mixed for better compatibility
    if(writingMode === "vertical-rl" || writingMode === "vertical-lr") {
      if(verticalTextDirection === "["){
        _addStyle.transform = "rotate(180deg)";
      }
    }
  }

  if(rainbow){
    _addStyle = {
      ..._addStyle,
      backgroundClip: "text", 
      background: clipColor ?? "linear-gradient(to right, #ff7e5f, #feb47b, #86a8e7, #91eae4)",
      backgroundSize: "200% 200%",
      color: "transparent",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      animation: "gradient-animation 1s ease-in-out infinite alternate"
    };
  }

  // Filter out custom props before passing to DOM element
  others = XProtect.NativeProps(others);

  let _rtn = (
    <div ref={textRef} style={{
      lineHeight: lineHeight ?? 1.6,
      fontSize: autoAdjust? calFontSize : fontSize,
      ..._addStyle,
      ...style
    }} {...others}>{_.isString(children) || _.isArray(children) || React.isValidElement(children)? children : String(children)}</div>
  );
  
  if(autoAdjust){
    return (
      <HStack fullWidth fullHeight forwardRef={containerRef}>
        {_rtn}
      </HStack>
    );
  }
  return _rtn;
}

export default XText;
